import { css } from '@emotion/css';
import { observer } from 'mobx-react';
import React, { useState } from 'react';

import { Button } from '../../../../../../components/Button';
import { Card } from '../../../../../../components/Card';
import { Checkbox } from '../../../../../../components/Checkbox';
import { Modal } from '../../../../../../components/Modal';
import { Color, FontSize } from '../../../../../../constants/Style';
import { square, toRGBA } from '../../../../../../helpers/Style';

type Props = {
  open: boolean;
  onAllow: () => void;
  onCancel?: () => void;
};

const titleStyle = css`
  margin-bottom: 8px;
  font-size: ${FontSize.Large};
  font-weight: 700;
  text-align: center;
`;

const confirmHrStyle = css`
  border: 0.5px solid ${Color.GrayscaleLight};
  opacity: 0.5;
`;

const subtitleStyle = css`
  margin-bottom: 16px;
  line-height: 1.6;
`;

const listStyle = css`
  padding-left: 1.25rem;
  margin: 10px 0 0;

  li + li {
    margin-top: 6px;
  }
`;

const warnStyle = css`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  margin: 14px 0 0;
  font-size: ${FontSize.SmallRegular};
  color: ${Color.FunctionalRedNeutral};
  background: ${toRGBA(Color.FunctionalRedNeutral, 0.08)};
  border-radius: 8px;
`;

const iconDotStyle = css`
  ${square(6)}

  display: inline-block;
  margin-right: 8px;
  background: ${Color.GrayscaleDark};
  border-radius: 999px;
`;

const buttonsStyle = css`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 10px;
  margin-top: 16px;
  cursor: pointer;
`;

/**
 * 外国人旅行者向け 事前同意ダイアログ
 * - 外側クリックでは閉じない（Modal に onClick を渡さない）
 * - 必須チェック 2 項目が揃うまで「続ける」ボタンは disabled
 */
export const ForeignTouristGateDialog = observer(({ open, onAllow, onCancel }: Props) => {
  const [agreeNoNHI, setAgreeNoNHI] = useState(false); // 必須1：日本の健康保険証を持っていない
  const [agreePurpose, setAgreePurpose] = useState(false); // 必須2：旅行中の急病/薬不足への診療を希望
  const [agreeWait, setAgreeWait] = useState(false); // 任意 ：1–2時間待ち了承

  const allowEnabled = agreeNoNHI && agreePurpose && agreeWait;

  return (
    <Modal visible={open}>
      <Card width={440}>
        {/* 見出し */}
        <div className={titleStyle}>For Foreign Tourists</div>
        <hr className={confirmHrStyle} />
        {/* リード文 */}
        <div className={subtitleStyle}>
          Our clinic offers outpatient consultations for travelers experiencing sudden illness while visiting Japan.
          This service is available by appointment only and is entirely self-paid.
        </div>

        {/* 注意書き（任意） */}
        <div className={warnStyle}>
          <span className={iconDotStyle} />
          This service is not covered by Japanese public insurance and will incur out-of-pocket expenses.
        </div>

        {/* チェック項目 */}
        <ul className={listStyle}>
          <li>
            <Checkbox
              checked={agreeNoNHI}
              onChange={e => setAgreeNoNHI(e.target.checked)}
              label="I do NOT have Japanese National Health Insurance."
            />
          </li>
          <li>
            <Checkbox
              checked={agreePurpose}
              onChange={e => setAgreePurpose(e.target.checked)}
              label="I would like to receive medical consultation for sudden illness or temporary medication shortages during my trip, excluding injuries."
            />
          </li>
          <li>
            <Checkbox
              checked={agreeWait}
              onChange={e => setAgreeWait(e.target.checked)}
              label="I understand that as the consultation will take place during regular outpatient hours, there may be a waiting time of approximately 1-2 hours."
            />
          </li>
        </ul>

        {/* アクション */}
        <div className={buttonsStyle}>
          <Button type="button" block disabled={!allowEnabled} onClick={onAllow}>
            I agree and continue
          </Button>
          <Button type="button" block theme="neutral" ghost onClick={onCancel ?? (() => {})}>
            Back
          </Button>
        </div>
      </Card>
    </Modal>
  );
});

export default ForeignTouristGateDialog;
